import { useQuery } from "@apollo/client";
import { COMPANY_MATCH_FILE_STATS } from "../../../apis/companyMatch/queries";
import { COMPANY_MATCH_STATUS_QUERY_INTERVAL_IN_MS } from "../../../components/filters/Whitespace/constants";
import { useSnackbar } from "../../../components/Snackbar";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { ICPFileUpload } from "../../../store/workspace/slices/workspaceICPSlice";
import { CompanyMatchStatusPayload } from "../../../types/companyMatch";
import { COMPANY_MATCH_CLIENT } from "../../../utils/consts";

interface UseCompanyMatchSyncProps {
  draftFilters?: CompanyFilter | ICPFileUpload;
  setDraftFilters: (filters: CompanyFilter | ICPFileUpload | undefined) => void;
  handleApplyFilters?: (filters?: CompanyFilter) => void;
}

export const useCompanyMatchSync = ({
  draftFilters,
  setDraftFilters,
  handleApplyFilters,
}: UseCompanyMatchSyncProps) => {
  const { showSuccess } = useSnackbar();

  /**
   * Query to get the company match status
   */
  useQuery<CompanyMatchStatusPayload>(COMPANY_MATCH_FILE_STATS, {
    variables: { fileId: draftFilters?.companyMatch?.fileId },
    // Skip the query if the company match is already processed or not uploaded
    skip:
      !draftFilters?.companyMatch?.fileId ||
      !!draftFilters?.companyMatch?.lastMatched,
    pollInterval: COMPANY_MATCH_STATUS_QUERY_INTERVAL_IN_MS,
    notifyOnNetworkStatusChange: true,
    context: { clientName: COMPANY_MATCH_CLIENT },
    onCompleted: (statsData: CompanyMatchStatusPayload) => {
      const data = {
        fileId: statsData.stats.fileId,
        invalidRows: statsData.stats.invalidRows,
        lastMatched: statsData.stats.lastMatched,
        matchedRows: statsData.stats.matchedRows,
        salesWins: statsData.stats.salesWins,
        salesLosses: statsData.stats.salesLosses,
        processedChunks: statsData.stats.processedChunks,
        totalChunks: statsData.stats.totalChunks,
        totalRows: statsData.stats.totalRows,
        uploaded: statsData.stats.uploaded,
      };

      if (data.lastMatched) {
        const updatedDraftFilters = {
          ...draftFilters,
          companyMatch: {
            ...draftFilters?.companyMatch,
            ...data,
          },
        };

        showSuccess(
          "Company match uploaded successfully, reloading data...",
          10_000
        );
        setDraftFilters(updatedDraftFilters);
        handleApplyFilters?.(updatedDraftFilters);
      } else {
        setDraftFilters({
          ...draftFilters,
          companyMatch: {
            ...draftFilters?.companyMatch,
            ...data,
          },
        });
      }
    },
  });

  return {};
};
